import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  Suspense,
  lazy,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";

import {
  getOutOfMeet,
  selectCurrentRoom,
  setRoomData,
  setUpdatePatientLine,
} from "@/context/features/room/roomSlice";
import {
  selectCurrentAuth,
  updateCredentials,
} from "@/context/features/auth/authSlice";

import { useJoinAndConnect } from "@/hooks/livekit/useJoinAndConnect";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import OpenPatient from "@/components/elements/open-patient-list";
import { useSidebar } from "@/components/ui/sidebar";
const JoinRequest = lazy(() => import("@/components/templates/join-request"));
const Room = lazy(() => import("@/components/templates/room"));
const Error = lazy(() => import("@/pages/errors/index"));

import { toaster } from "@/utils/toaster";
import { useCurrentUserInfoLazyQuery } from "@/graphql/generated";
import { Role } from "@/types/role.enum";
import JoinSkeleton from "@/components/skeletons/join-skeleton";
import SubscriptionsMount from "@/hooks/room/SubscriptionsMount";

function resolveLivekitWsUrl(): string {
  const isHttps =
    typeof window !== "undefined" && window.location.protocol === "https:";
  const wsProto = isHttps ? "wss" : "ws";
  const currentHost =
    typeof window !== "undefined" ? window.location.host : "localhost:5173";

  if (import.meta.env.DEV) {
    const fromEnv = import.meta.env.VITE_LK_WS_URL as string | undefined;
    if (fromEnv?.trim()) return fromEnv.trim();

    const raw = (
      import.meta.env.VITE_LIVE_LK ??
      import.meta.env.VITE_LIVEKITSERVER ??
      "localhost:7880"
    )
      .toString()
      .trim();

    if (raw.startsWith("ws://") || raw.startsWith("wss://")) {
      return raw;
    }

    if (raw.startsWith("/")) {
      return `${wsProto}://${currentHost}${raw}`;
    }

    if (raw === "livekit") {
      console.warn(
        "[LiveKit] VITE_LIVEKITSERVER is 'livekit' which is not browser-resolvable; falling back to localhost:7880"
      );
      return `${wsProto}://localhost:7880`;
    }

    return `${wsProto}://${raw}`;
  }

  const raw =
    typeof window !== "undefined"
      ? window.location.host + "/livekit"
      : "localhost:5173".toString().trim();

  console.log("raw", raw);

  if (raw.startsWith("ws://") || raw.startsWith("wss://")) {
    return raw;
  }

  if (raw.startsWith("/")) {
    return `${wsProto}://${currentHost}${raw}`;
  }

  return `${wsProto}://${raw}`;
}

function MainPage() {
  const { meetId } = useParams<{ meetId: string }>();
  const { isInMeet, wsConnected } = useSelector(selectCurrentRoom);
  const { user, jwt, global_error } = useSelector(selectCurrentAuth);
  const wsUrl = useMemo(() => {
    return resolveLivekitWsUrl();
  }, []);
  const joinOptions = useMemo(
    () => ({
      wsUrl,
      bioArcRoomId: meetId,
    }),
    [wsUrl, meetId]
  );
  const roomController = useJoinAndConnect(joinOptions);
  const { setLivekitContext } = useLivekitContext();

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { setOpen, isMobile, setOpenMobile } = useSidebar();

  const [getCurrentUserInfo, { data, loading, error }] =
    useCurrentUserInfoLazyQuery();

  useEffect(() => {
    document.title = t("onlineVisit");
  }, [t]);

  useEffect(() => {
    if (jwt && user?.role && user.role.toUpperCase() !== Role.HOST) {
      getCurrentUserInfo();
    }
  }, [jwt, user?.role, getCurrentUserInfo]);

  useEffect(() => {
    const current = data?.user?.currentUserInfo;
    if (current) {
      dispatch(
        updateCredentials({
          role: current.role,
          approved: current.approved,
          isDone: current.isDone,
          isPaused: current.isPaused,
        })
      );
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (roomController) setLivekitContext(roomController);
    return () => setLivekitContext(null);
  }, [setLivekitContext, roomController]);

  const lastAdminIdRef = useRef<string | null>(null);
  useEffect(() => {
    const rd = roomController.roomData;
    const adminId = rd?.adminId ? String(rd.adminId) : null;
    if (!adminId || !rd) return;
    if (lastAdminIdRef.current === adminId) return;
    lastAdminIdRef.current = adminId;
    dispatch(setRoomData(rd));
  }, [dispatch, roomController.roomData]);

  const runExitOnce = useCallback(
    (
      toastType: "error" | "info" | "default",
      actionType: "kick" | "hold",
      messageKey: string
    ) => {
      dispatch(updateCredentials({ approved: false }));
      if (actionType === "kick") {
        dispatch(updateCredentials({ isDone: true }));
      }
      dispatch(getOutOfMeet());
      setOpenMobile(false);
      setOpen(false);

      if (isMobile) setOpenMobile(false);

      toaster(toastType, t(messageKey));
    },
    [dispatch, isMobile, setOpen, setOpenMobile, t]
  );
  const handleJoinRequest = useCallback(
    (data: { userId: string; fullName: string }) => {
      if (!data?.userId) return;
      dispatch(setUpdatePatientLine(true));
      toaster("default", t("new_user"), {
        position: isMobile ? "top-right" : "bottom-right",
        description: `${data.fullName} ${t("wants_to_join_meet")}`,
        descriptionClassName: "font-medium dark:text-white mt-2 pb-10",
        action: (
          <OpenPatient
            toastId={`${data.userId}-wants-join`}
            userId={data.userId}
          />
        ),
        id: `${data.userId}-wants-join`,
      });
    },
    [t, dispatch, isMobile]
  );

  const fatalError = useMemo(() => {
    const livekitErr =
      roomController.joinLivekitError &&
      roomController.joinLivekitError !== "Room not found"
        ? roomController.joinLivekitError
        : null;
    if (global_error) {
      return {
        component: (
          <Error
            errorCode={global_error.errorCode}
            customTitle={global_error.message}
          />
        ),
        title: livekitErr,
      };
    }

    if (livekitErr === "Failed to fetch")
      return {
        component: <Error errorCode={"connection"} customTitle={livekitErr} />,
        title: livekitErr,
      };
    else if (livekitErr === "Room not found")
      return {
        component: <Error errorCode={400} customTitle={livekitErr} />,
        title: livekitErr,
      };
    else if (error)
      return {
        component: <Error errorCode={400} customTitle={error?.message} />,
        title: error?.message || livekitErr || null,
      };
    else return null;
  }, [roomController.joinLivekitError, error, global_error]);

  const lastToastRef = useRef<string | null>(null);
  useEffect(() => {
    if (fatalError?.title && lastToastRef.current !== fatalError.title) {
      lastToastRef.current = fatalError.title;
      toaster("error", t(fatalError.title));
    }
  }, [fatalError, t]);

  // ---- RENDER ----
  if (!meetId) return null;

  if (fatalError) {
    return (
      <Suspense fallback={<JoinSkeleton />}>{fatalError.component}</Suspense>
    );
  }

  if (loading) {
    return <JoinSkeleton />;
  }

  return (
    <Suspense fallback={<JoinSkeleton />}>
      {isInMeet ? <Room /> : <JoinRequest meetId={meetId} />}
      {user && (
        <SubscriptionsMount
          key={`${jwt}|${user.room_name}|${user.id}`}
          wsConnected={wsConnected}
          role={user.role}
          roomName={user.room_name}
          userId={user.id.toString()}
          handleJoinRequest={handleJoinRequest}
          runExitOnce={runExitOnce}
        />
      )}
    </Suspense>
  );
}

export default memo(MainPage);
